import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import CytoscapeComponent from 'react-cytoscapejs';
import cytoscape from 'cytoscape';
import { FixedSizeList, ListChildComponentProps } from 'react-window';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  Tooltip,
  useTheme,
} from '@mui/material';
import { Refresh } from '@mui/icons-material';
import { apiClient } from 'utils/ApiClient';
import { Logger } from 'utils/Logger';

export interface GraphNode {
  id: string;
  label: string;
  kind?: string;
  source_file?: string;
  loc?: string;
  community?: number | null;
  confidence?: string;
}

export interface GraphEdge {
  source: string;
  target: string;
  relation?: string;
  confidence?: string;
}

export interface GraphData {
  tag: string;
  nodes: GraphNode[];
  edges: GraphEdge[];
}

const LAYOUT_OPTIONS = [
  { value: 'cose', label: 'Cose (force)' },
  { value: 'circle', label: 'Circle' },
  { value: 'grid', label: 'Grid' },
  { value: 'concentric', label: 'Concentric' },
  { value: 'breadthfirst', label: 'Breadthfirst' },
  { value: 'table', label: 'Table' },
] as const;

type LayoutName = (typeof LAYOUT_OPTIONS)[number]['value'];
type GraphLayoutName = Exclude<LayoutName, 'table'>;

/**
 * Builds the Cytoscape stylesheet. In performance mode (large graphs) we make
 * the most expensive rendering features conditional:
 *   - Node labels only draw once zoomed in (`min-zoomed-font-size`), so a
 *     fully zoomed-out view of 900+ nodes doesn't paint hundreds of labels.
 *   - Edges use the `haystack` renderer (far cheaper than `bezier`) and drop
 *     their per-edge labels + arrowheads, which are the single biggest cost.
 */
const buildStylesheet = (performanceMode: boolean) => [
  {
    selector: 'node',
    style: {
      label: 'data(label)',
      'text-valign': 'center',
      'text-halign': 'center',
      'font-size': '10px',
      'background-color': '#5c6bc0',
      color: '#fff',
      'text-outline-color': '#333',
      'text-outline-width': 1,
      width: performanceMode ? 18 : 28,
      height: performanceMode ? 18 : 28,
      'min-zoomed-font-size': performanceMode ? 14 : 0,
    },
  },
  {
    selector: 'node.dimmed',
    style: {
      opacity: 0.15,
    },
  },
  {
    selector: 'node.highlighted',
    style: {
      'background-color': '#ff7043',
      width: performanceMode ? 26 : 36,
      height: performanceMode ? 26 : 36,
      'font-size': '11px',
      'min-zoomed-font-size': 0,
      'z-index': 10,
    },
  },
  {
    selector: 'edge',
    style: performanceMode
      ? {
          width: 1,
          'line-color': '#90a4ae',
          'curve-style': 'haystack',
          'haystack-radius': 0,
          opacity: 0.55,
        }
      : {
          width: 1.5,
          'line-color': '#90a4ae',
          'target-arrow-color': '#90a4ae',
          'target-arrow-shape': 'triangle',
          'curve-style': 'bezier',
          label: 'data(relation)',
          'font-size': '8px',
          color: '#78909c',
          'text-rotation': 'autorotate',
        },
  },
  {
    selector: 'edge.dimmed',
    style: {
      opacity: 0.08,
    },
  },
];

// At/above this node count the graph view switches to Table by default.
const LARGE_GRAPH_NODE_THRESHOLD = 500;
// At/above this node count we enable Cytoscape performance mode when rendering
// any visual layout (lighter styles, no animation, viewport texturing).
const PERFORMANCE_MODE_NODE_THRESHOLD = 500;
// Above this node count the visual layouts are disabled entirely — only the
// Table layout is available, since force/graph layouts aren't usable at scale.
const GRAPH_LAYOUT_MAX_NODES = 600;

const VIRTUAL_ROW_HEIGHT = 36;
const VIRTUAL_HEADER_HEIGHT = 40;
// Max time incoming stream batches are buffered before a single React flush.
// Keeps the main thread free during streaming (few renders/sec, not per-batch).
const STREAM_FLUSH_INTERVAL_MS = 150;

interface VirtualColumn<T> {
  key: string;
  header: string;
  value: (row: T) => string;
  mono?: boolean;
  flex: string;
}

interface VirtualizedTableProps<T> {
  columns: VirtualColumn<T>[];
  rows: T[];
  rowKey: (row: T, index: number) => string;
  height: number;
  emptyMessage: string;
}

/**
 * A lightweight, windowed table built on `react-window`. Only the rows visible
 * in the scroll viewport are mounted to the DOM, so a 10k-row dataset renders
 * as cheaply as a 20-row one — this is what keeps the UI responsive while the
 * graph streams in (a plain MUI <table> would mount every row and freeze).
 */
function VirtualizedTable<T>({ columns, rows, rowKey, height, emptyMessage }: VirtualizedTableProps<T>) {
  const theme = useTheme();
  const borderColor = theme.palette.divider;
  const headerBg = theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)';
  const gridTemplateColumns = columns.map((c) => c.flex).join(' ');

  const Row = ({ index, style }: ListChildComponentProps) => {
    const row = rows[index];
    return (
      <Box
        key={rowKey(row, index)}
        style={style}
        sx={{
          display: 'grid',
          gridTemplateColumns,
          alignItems: 'center',
          columnGap: 1,
          px: 1.5,
          boxSizing: 'border-box',
          borderBottom: `1px solid ${borderColor}`,
          '&:hover': { backgroundColor: theme.palette.action.hover },
        }}
      >
        {columns.map((col) => {
          const text = col.value(row);
          return (
            <Box
              key={col.key}
              title={text}
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                pr: 1,
                fontFamily: col.mono ? 'monospace' : undefined,
                fontSize: col.mono ? '0.72rem' : '0.8125rem',
              }}
            >
              {text}
            </Box>
          );
        })}
      </Box>
    );
  };

  return (
    <Paper elevation={0} variant="outlined" sx={{ overflow: 'hidden' }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns,
          columnGap: 1,
          px: 1.5,
          height: VIRTUAL_HEADER_HEIGHT,
          alignItems: 'center',
          backgroundColor: headerBg,
          borderBottom: `1px solid ${borderColor}`,
          fontWeight: 700,
          fontSize: '0.8125rem',
        }}
      >
        {columns.map((col) => (
          <Box key={col.key} sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {col.header}
          </Box>
        ))}
      </Box>
      {rows.length === 0 ? (
        <Box sx={{ py: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {emptyMessage}
          </Typography>
        </Box>
      ) : (
        <FixedSizeList
          height={height}
          width="100%"
          itemCount={rows.length}
          itemSize={VIRTUAL_ROW_HEIGHT}
          overscanCount={10}
        >
          {Row}
        </FixedSizeList>
      )}
    </Paper>
  );
}

const NODE_COLUMNS: VirtualColumn<GraphNode>[] = [
  { key: 'label', header: 'Label', flex: '1.4fr', value: (n) => n.label || n.id },
  { key: 'kind', header: 'Kind', flex: '0.8fr', value: (n) => n.kind || '—' },
  { key: 'source_file', header: 'Source File', flex: '1.7fr', value: (n) => n.source_file || '—' },
  { key: 'loc', header: 'Location', flex: '0.7fr', value: (n) => n.loc || '—' },
  { key: 'community', header: 'Community', flex: '0.7fr', value: (n) => (n.community != null ? String(n.community) : '—') },
  { key: 'confidence', header: 'Confidence', flex: '0.8fr', value: (n) => n.confidence || '—' },
  { key: 'id', header: 'ID', flex: '1.4fr', mono: true, value: (n) => n.id },
];

const EDGE_COLUMNS: VirtualColumn<GraphEdge>[] = [
  { key: 'source', header: 'Source', flex: '1.6fr', mono: true, value: (e) => e.source },
  { key: 'relation', header: 'Relation', flex: '0.9fr', value: (e) => e.relation || '—' },
  { key: 'target', header: 'Target', flex: '1.6fr', mono: true, value: (e) => e.target },
  { key: 'confidence', header: 'Confidence', flex: '0.8fr', value: (e) => e.confidence || '—' },
];

interface KnowledgeGraphViewProps {
  projectName: string;
}

export const KnowledgeGraphView: React.FC<KnowledgeGraphViewProps> = ({ projectName }) => {
  const theme = useTheme();
  const cyRef = useRef<cytoscape.Core | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const pendingNodesRef = useRef<GraphNode[]>([]);
  const pendingEdgesRef = useRef<GraphEdge[]>([]);
  const flushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [graphData, setGraphData] = useState<GraphData | null>(null);
  // `loading` is only true until the first (meta) line arrives; after that we
  // switch to the determinate `streaming` progress state.
  const [loading, setLoading] = useState(true);
  const [streaming, setStreaming] = useState(false);
  const [totals, setTotals] = useState<{ nodes: number; edges: number }>({ nodes: 0, edges: 0 });
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [layoutName, setLayoutName] = useState<LayoutName>('cose');
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(() =>
    typeof window !== 'undefined' ? window.innerHeight : 800,
  );

  useEffect(() => {
    const onResize = () => setViewportHeight(window.innerHeight);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const clearFlushTimer = useCallback(() => {
    if (flushTimerRef.current != null) {
      clearTimeout(flushTimerRef.current);
      flushTimerRef.current = null;
    }
  }, []);

  const fetchGraph = useCallback(async () => {
    // Cancel any in-flight stream (refresh / project change) before starting.
    abortRef.current?.abort();
    clearFlushTimer();
    pendingNodesRef.current = [];
    pendingEdgesRef.current = [];
    const abort = new AbortController();
    abortRef.current = abort;

    setLoading(true);
    setStreaming(false);
    setError(null);
    setGraphData(null);
    setTotals({ nodes: 0, edges: 0 });

    // Drain buffered node/edge batches into a single state update. Batching the
    // flushes (vs. per-message) keeps React re-renders to a few per second so
    // the main thread stays responsive while data streams in.
    const flushPending = () => {
      clearFlushTimer();
      if (abort.signal.aborted) return;
      const addNodes = pendingNodesRef.current;
      const addEdges = pendingEdgesRef.current;
      if (addNodes.length === 0 && addEdges.length === 0) return;
      pendingNodesRef.current = [];
      pendingEdgesRef.current = [];
      setGraphData((prev) =>
        prev
          ? {
              ...prev,
              nodes: addNodes.length ? prev.nodes.concat(addNodes) : prev.nodes,
              edges: addEdges.length ? prev.edges.concat(addEdges) : prev.edges,
            }
          : prev,
      );
    };
    const scheduleFlush = () => {
      if (flushTimerRef.current == null) {
        flushTimerRef.current = setTimeout(flushPending, STREAM_FLUSH_INTERVAL_MS);
      }
    };

    const encoded = encodeURIComponent(projectName);
    try {
      await apiClient.stream(
        `/anamnesis/knowledge-graphs/${encoded}/stream`,
        (msg) => {
          switch (msg?.type) {
            case 'meta': {
              const nodeCount = Number(msg.nodeCount) || 0;
              const edgeCount = Number(msg.edgeCount) || 0;
              setTotals({ nodes: nodeCount, edges: edgeCount });
              // >600 nodes: only the table is usable, so open in and lock to table.
              setLayoutName(nodeCount > LARGE_GRAPH_NODE_THRESHOLD ? 'table' : 'cose');
              setGraphData({ tag: String(msg.tag || projectName), nodes: [], edges: [] });
              setStreaming(true);
              setLoading(false);
              break;
            }
            case 'nodes':
              pendingNodesRef.current.push(...(msg.items as GraphNode[]));
              scheduleFlush();
              break;
            case 'edges':
              pendingEdgesRef.current.push(...(msg.items as GraphEdge[]));
              scheduleFlush();
              break;
            case 'error':
              setError(String(msg.message || 'Failed to stream knowledge graph'));
              break;
            case 'done':
            default:
              break;
          }
        },
        { signal: abort.signal },
      );
      // Flush anything still buffered when the stream ends.
      flushPending();
    } catch (err) {
      if (abort.signal.aborted) return; // superseded by a newer request
      Logger.error('KnowledgeGraphView: failed to stream graph', err);
      setError(err instanceof Error ? err.message : 'Failed to load knowledge graph');
      setGraphData(null);
    } finally {
      if (!abort.signal.aborted) {
        setStreaming(false);
        setLoading(false);
      }
    }
  }, [projectName, clearFlushTimer]);

  useEffect(() => {
    fetchGraph();
    return () => {
      abortRef.current?.abort();
      clearFlushTimer();
    };
  }, [fetchGraph, refreshTrigger, clearFlushTimer]);

  // Derived from the meta line so styling stays stable while nodes stream in.
  const totalNodeCount = totals.nodes || (graphData?.nodes.length ?? 0);
  const performanceMode = totalNodeCount > PERFORMANCE_MODE_NODE_THRESHOLD;
  // Above the limit, visual layouts are disabled and only Table is allowed.
  const graphLayoutsDisabled = totalNodeCount > GRAPH_LAYOUT_MAX_NODES;
  const stylesheet = useMemo(() => buildStylesheet(performanceMode), [performanceMode]);

  // Keep the selection valid if the graph is too large for visual layouts
  // (e.g. user had a visual layout selected before the large graph loaded).
  useEffect(() => {
    if (graphLayoutsDisabled && layoutName !== 'table') {
      setLayoutName('table');
    }
  }, [graphLayoutsDisabled, layoutName]);

  const elements = useMemo((): cytoscape.ElementDefinition[] => {
    if (!graphData) return [];
    const nodeElements: cytoscape.ElementDefinition[] = graphData.nodes.map((node) => ({
      data: {
        id: node.id,
        label: String(node.label || node.id),
        kind: node.kind || '',
        source_file: node.source_file || '',
      },
    }));
    const edgeElements: cytoscape.ElementDefinition[] = graphData.edges.map((edge, index) => ({
      data: {
        id: `edge-${index}-${edge.source}-${edge.target}`,
        source: edge.source,
        target: edge.target,
        relation: edge.relation || '',
      },
    }));
    return [...nodeElements, ...edgeElements];
  }, [graphData]);

  const applySearchHighlight = useCallback((cy: cytoscape.Core, term: string) => {
    const normalized = term.trim().toLowerCase();
    // Batch all class mutations so the renderer only recomputes/redraws once.
    cy.batch(() => {
      cy.nodes().removeClass('dimmed highlighted');
      cy.edges().removeClass('dimmed');

      if (!normalized) return;

      const matching = cy.nodes().filter((node) => {
        const label = String(node.data('label') || '').toLowerCase();
        const kind = String(node.data('kind') || '').toLowerCase();
        const sourceFile = String(node.data('source_file') || '').toLowerCase();
        return label.includes(normalized) || kind.includes(normalized) || sourceFile.includes(normalized);
      });

      if (matching.length === 0) {
        cy.nodes().addClass('dimmed');
        cy.edges().addClass('dimmed');
        return;
      }

      matching.addClass('highlighted');
      cy.nodes().difference(matching).addClass('dimmed');
      // connectedEdges() is far cheaper than iterating every edge in the graph.
      cy.edges().difference(matching.connectedEdges()).addClass('dimmed');
    });
  }, []);

  const runLayout = useCallback((cy: cytoscape.Core, name: GraphLayoutName, lightweight: boolean) => {
    const animate = !lightweight;
    const layoutOptions: Record<GraphLayoutName, cytoscape.LayoutOptions> = {
      cose: {
        name: 'cose',
        animate,
        padding: 30,
        nodeRepulsion: () => 8000,
        // Cap iterations and skip label-aware sizing on large graphs so the
        // O(n^2) force simulation finishes quickly instead of hanging.
        numIter: lightweight ? 350 : 1000,
        nodeDimensionsIncludeLabels: false,
        randomize: lightweight,
      },
      circle: { name: 'circle', animate, padding: 30 },
      grid: { name: 'grid', animate, padding: 30 },
      concentric: { name: 'concentric', animate, padding: 30 },
      breadthfirst: { name: 'breadthfirst', animate, padding: 30, directed: true },
    };
    cy.layout(layoutOptions[name]).run();
  }, []);

  const isTableView = layoutName === 'table';

  const filteredNodes = useMemo(() => {
    if (!graphData) return [];
    const normalized = searchTerm.trim().toLowerCase();
    if (!normalized) return graphData.nodes;
    return graphData.nodes.filter((node) => {
      const fields = [
        node.id,
        node.label,
        node.kind,
        node.source_file,
        node.loc,
        node.confidence,
        node.community != null ? String(node.community) : '',
      ];
      return fields.some((field) => String(field || '').toLowerCase().includes(normalized));
    });
  }, [graphData, searchTerm]);

  const filteredEdges = useMemo(() => {
    if (!graphData) return [];
    const normalized = searchTerm.trim().toLowerCase();
    if (!normalized) return graphData.edges;
    return graphData.edges.filter((edge) => {
      const fields = [edge.source, edge.target, edge.relation, edge.confidence];
      return fields.some((field) => String(field || '').toLowerCase().includes(normalized));
    });
  }, [graphData, searchTerm]);

  // Pixel heights for the two windowed lists (react-window needs a number).
  const nodesListHeight = Math.max(180, Math.round(viewportHeight * 0.34));
  const edgesListHeight = Math.max(140, Math.round(viewportHeight * 0.24));

  const handleCyInit = useCallback(
    (cy: cytoscape.Core) => {
      cyRef.current = cy;
      if (layoutName !== 'table') {
        runLayout(cy, layoutName, performanceMode);
        applySearchHighlight(cy, searchTerm);
      }
    },
    [layoutName, runLayout, applySearchHighlight, searchTerm, performanceMode],
  );

  useEffect(() => {
    const cy = cyRef.current;
    // Defer layout until the stream has fully arrived — re-running a force
    // layout on every incoming batch would be far slower than one final pass.
    if (!cy || loading || streaming || isTableView) return;
    runLayout(cy, layoutName, performanceMode);
  }, [layoutName, elements, loading, streaming, runLayout, isTableView, performanceMode]);

  useEffect(() => {
    const cy = cyRef.current;
    if (!cy || isTableView) return;
    applySearchHighlight(cy, searchTerm);
  }, [searchTerm, elements, applySearchHighlight, isTableView]);

  const toolbarSurfaceSx = {
    display: 'flex',
    flexDirection: 'row' as const,
    gap: { xs: 1, md: 2 },
    alignItems: 'center',
    mb: 2,
    p: 1.5,
    borderRadius: 2,
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.45)' : 'rgba(0,0,0,0.06)',
  };

  return (
    <Box>
      <Box sx={toolbarSurfaceSx}>
        <TextField
          label="Search Knowledge Graph"
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ flexGrow: 1, minWidth: '220px' }}
        />
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Layout</InputLabel>
          <Select
            value={layoutName}
            label="Layout"
            onChange={(e) => setLayoutName(e.target.value as LayoutName)}
          >
            {LAYOUT_OPTIONS.map((opt) => (
              <MenuItem
                key={opt.value}
                value={opt.value}
                disabled={graphLayoutsDisabled && opt.value !== 'table'}
              >
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Tooltip title="Refresh">
          <Button
            variant="contained"
            color="secondary"
            onClick={() => setRefreshTrigger((prev) => prev + 1)}
            size="small"
            sx={{ minWidth: 36, px: 1, flexShrink: 0 }}
            aria-label="Refresh"
          >
            <Refresh fontSize="small" />
          </Button>
        </Tooltip>
      </Box>

      <Box
        sx={{
          position: 'relative',
          height: { xs: '60vh', md: '70vh' },
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          overflow: 'hidden',
          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.6)',
        }}
      >
        {loading && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2,
              backgroundColor: 'rgba(0,0,0,0.15)',
            }}
          >
            <CircularProgress color="secondary" />
          </Box>
        )}
        {streaming && !loading && (
          <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 3 }}>
            <LinearProgress
              color="secondary"
              variant={totals.nodes > 0 ? 'determinate' : 'indeterminate'}
              value={
                totals.nodes > 0
                  ? Math.min(100, (((graphData?.nodes.length ?? 0) + (graphData?.edges.length ?? 0)) /
                      (totals.nodes + totals.edges || 1)) * 100)
                  : undefined
              }
            />
          </Box>
        )}
        {!error && !isTableView && streaming && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1.5,
              zIndex: 2,
            }}
          >
            <CircularProgress color="secondary" />
            <Typography variant="body2" color="text.secondary" textAlign="center">
              Loading graph… {graphData?.nodes.length ?? 0} / {totals.nodes} nodes,{' '}
              {graphData?.edges.length ?? 0} / {totals.edges} edges
            </Typography>
            <Typography variant="caption" color="text.secondary" textAlign="center">
              The layout is drawn once all data has arrived.
            </Typography>
          </Box>
        )}
        {error && !loading && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 3,
            }}
          >
            <Typography color="error" textAlign="center">
              {error}
            </Typography>
          </Box>
        )}
        {!error && graphData && graphData.nodes.length === 0 && !loading && !streaming && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography color="text.secondary">No graph data available for this project.</Typography>
          </Box>
        )}
        {!error && isTableView && graphData && graphData.nodes.length > 0 && !loading && (
          <Box sx={{ height: '100%', overflow: 'auto', p: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Nodes ({filteredNodes.length})
            </Typography>
            <Box sx={{ mb: 3 }}>
              <VirtualizedTable
                columns={NODE_COLUMNS}
                rows={filteredNodes}
                rowKey={(node) => node.id}
                height={nodesListHeight}
                emptyMessage="No nodes match your search."
              />
            </Box>

            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Edges ({filteredEdges.length})
            </Typography>
            <VirtualizedTable
              columns={EDGE_COLUMNS}
              rows={filteredEdges}
              rowKey={(edge, index) => `${edge.source}-${edge.target}-${index}`}
              height={edgesListHeight}
              emptyMessage="No edges match your search."
            />
          </Box>
        )}
        {!error && !isTableView && !streaming && elements.length > 0 && (
          <CytoscapeComponent
            elements={elements}
            stylesheet={stylesheet}
            cy={handleCyInit}
            style={{ width: '100%', height: '100%' }}
            wheelSensitivity={0.2}
            hideEdgesOnViewport={performanceMode}
            textureOnViewport={performanceMode}
            motionBlur={false}
            pixelRatio={performanceMode ? 1 : 'auto'}
          />
        )}
      </Box>

      {graphData && !loading && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          {isTableView
            ? `Showing ${filteredNodes.length} of ${graphData.nodes.length} nodes, ${filteredEdges.length} of ${graphData.edges.length} edges`
            : `${graphData.nodes.length} nodes, ${graphData.edges.length} edges`}
          {isTableView && graphLayoutsDisabled
            ? ` · Visual layouts are disabled for graphs over ${GRAPH_LAYOUT_MAX_NODES} nodes; use Search to inspect the table.`
            : ''}
          {!isTableView && performanceMode
            ? ' · Performance mode on (labels appear as you zoom in, edge labels hidden). Use Search or the Table layout to inspect details.'
            : ''}
        </Typography>
      )}
    </Box>
  );
};

export default KnowledgeGraphView;
